import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, TextField, Button, Typography, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Checkbox } from "@mui/material";
import { toast } from "react-toastify";
import { handleSave } from "../utils/handleSave";
import { createTempId } from "../utils/createTempId";
import { axios_tenant } from "../../../../axios";
import { FIXED_SELECT_FIELD_SX, TABLE_SCROLL_CONTAINER_SX, KOT_SELECT_MENU_PROPS, hasValue, isRowComplete } from "../utils/formHelpers";
import { useKotFormDirty } from "../utils/useKotFormDirty";

// All three fields are mandatory for a foreign associate row.
const FOREIGN_ASSOCIATE_REQUIRED_FIELDS = ["country", "associateName", "associateEmails"];
const foreignAssociateRowHasData = (row) => FOREIGN_ASSOCIATE_REQUIRED_FIELDS.some((field) => hasValue(row?.[field]));

const ForeignAssociates = ({ mode }) => {
  const isView = mode === "view";
  const dispatch = useDispatch();
  const savedData = useSelector((state) => state.kotClient.foreignAssociates);
  const clientId = useSelector((state) => state.kotClient?.clientId);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingMasters, setIsLoadingMasters] = useState(false);
  const [mastersError, setMastersError] = useState("");
  const [associatesByCountry, setAssociatesByCountry] = useState({});
  const { isDirty, markDirty, markSaved, markReset, syncClean } = useKotFormDirty();

  const normalizeEmailsToList = (emails) => {
    if (!emails) return [];
    const rawList = Array.isArray(emails) ? emails : [emails];
    return rawList
      .flatMap((e) => String(e).split(","))
      .map((e) => e.trim())
      .filter(Boolean);
  };

  // With nothing saved, edit mode still seeds one blank row so there is somewhere to type.
  // View mode must not: a read-only section with no data would otherwise render a row of
  // empty disabled inputs rather than saying it is empty.
  const buildRows = (nextRows = []) => {
    if (nextRows.length > 0) {
      return nextRows.map((row) => ({ ...row, associateEmails: normalizeEmailsToList(row.associateEmails) }));
    }

    if (isView) {
      return [];
    }

    return [
      {
        id: createTempId(),
        country: "",
        associateName: "",
        associateEmails: [],
      },
    ];
  };

  const [rows, setRows] = useState(buildRows(savedData));

  const [selectedRows, setSelectedRows] = useState([]);
  useEffect(() => {
    setRows(buildRows(savedData));
    setSelectedRows([]);
    syncClean();
  }, [clientId, savedData]);

  useEffect(() => {
    const fetchMasters = async () => {
      try {
        setIsLoadingMasters(true);
        setMastersError("");
        const res = await axios_tenant.get("/kot/foreign-associates");
        setAssociatesByCountry(res?.data && typeof res.data === "object" ? res.data : {});
      } catch (e) {
        setMastersError("Failed to load foreign associates master list");
        setAssociatesByCountry({});
      } finally {
        setIsLoadingMasters(false);
      }
    };
    fetchMasters();
  }, []);

  const availableCountries = useMemo(() => {
    return Object.keys(associatesByCountry || {}).sort((a, b) => a.localeCompare(b));
  }, [associatesByCountry]);

  /**
   * Country Change
   */
  const updateCountry = (rowId, value) => {
    markDirty();
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;

        const associates = associatesByCountry[value] || [];

        // Auto-fill if only 1 associate
        if (associates.length === 1) {
          return {
            ...row,
            country: value,
            associateName: associates[0].name,
            associateEmails: normalizeEmailsToList(associates[0].emails),
          };
        }

        return {
          ...row,
          country: value,
          associateName: "",
          associateEmails: [],
        };
      }),
    );
  };

  /**
   * Associate Change
   */
  const updateAssociateName = (rowId, value) => {
    markDirty();
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;

        const associates = associatesByCountry[row.country] || [];
        const selected = associates.find((a) => a.name === value);

        return {
          ...row,
          associateName: value,
          associateEmails: selected ? normalizeEmailsToList(selected.emails) : [],
        };
      }),
    );
  };

  /**
   * Add Row
   */
  const addRow = () => {
    // Allow a new row only when existing rows have all mandatory fields filled.
    if (rows.some((row) => !isRowComplete(row, FOREIGN_ASSOCIATE_REQUIRED_FIELDS))) {
      toast.warn("Complete the mandatory fields in the current row before adding another.");
      return;
    }

    markDirty();
    setRows((prev) => [
      ...prev,
      {
        id: createTempId(),
        country: "",
        associateName: "",
        associateEmails: [],
      },
    ]);
  };

  /**
   * Selection
   */
  const handleSelectRow = (id) => {
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  };

  const handleSelectAll = (checked) => {
    setSelectedRows(checked ? rows.map((r) => r.id) : []);
  };

  const deleteSelectedRows = () => {
    markDirty();
    setRows((prev) => prev.filter((row) => !selectedRows.includes(row.id)));
    setSelectedRows([]);
  };

  /**
   * Save
   */
  const saveData = async () => {
    // Validate mandatory fields on submit for any started row (empty rows are ignored).
    if (rows.some((row) => foreignAssociateRowHasData(row) && !isRowComplete(row, FOREIGN_ASSOCIATE_REQUIRED_FIELDS))) {
      toast.error("Please complete all mandatory fields before saving.");
      return;
    }

    setIsSaving(true);
    try {
      const result = await handleSave(dispatch, "foreignAssociates", rows, clientId, savedData);
      if (result?.status !== "error") {
        markSaved();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    markReset();
    setRows(buildRows(savedData));
    setSelectedRows([]);
  };

  return (
    <Box p={4} bgcolor="#fff" borderRadius={2}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h3">Foreign Associates</Typography>

        {!isView && selectedRows.length > 0 && (
          <Button variant="contained" color="error" onClick={deleteSelectedRows}>
            Delete ({selectedRows.length})
          </Button>
        )}
      </Box>

      {/* Table */}
      <Box sx={TABLE_SCROLL_CONTAINER_SX}>
        <Table sx={{ minWidth: 760, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              {!isView && (
                <TableCell padding="checkbox" sx={{ width: 56 }}>
                  <Checkbox data-ignore-dirty="true" checked={rows.length > 0 && selectedRows.length === rows.length} indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length} onChange={(e) => handleSelectAll(e.target.checked)} />
                </TableCell>
              )}

              <TableCell sx={{ width: 220, minWidth: 220 }}>Country</TableCell>
              <TableCell sx={{ width: 280, minWidth: 280 }}>Associate Name</TableCell>
              <TableCell sx={{ width: 320, minWidth: 320 }}>Email</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Reachable in view mode whenever the section has no data, and in edit mode
                once every row has been deleted. */}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3 + (isView ? 0 : 1)} align="center" sx={{ py: 5, color: "text.secondary", borderBottom: "none" }}>
                  <Typography>No records to show</Typography>
                </TableCell>
              </TableRow>
            )}

            {rows.map((row) => {
              const associates = associatesByCountry[row.country] || [];
              const isMastersReady = !isLoadingMasters && availableCountries.length > 0;

              return (
                <TableRow key={row.id} selected={selectedRows.includes(row.id)}>
                  {/* Checkbox */}
                  {!isView && (
                    <TableCell padding="checkbox" sx={{ width: 56, verticalAlign: "top" }}>
                      <Checkbox data-ignore-dirty="true" checked={selectedRows.includes(row.id)} onChange={() => handleSelectRow(row.id)} />
                    </TableCell>
                  )}

                  {/* Country */}
                  <TableCell sx={{ width: 220, minWidth: 220, verticalAlign: "top" }}>
                    <TextField disabled={isView || !isMastersReady} select size="small" fullWidth sx={FIXED_SELECT_FIELD_SX} label={isLoadingMasters ? "Loading..." : "Select Country"} value={row.country} onChange={(e) => updateCountry(row.id, e.target.value)} helperText={mastersError || (!isMastersReady ? "Master list is required" : "")} SelectProps={KOT_SELECT_MENU_PROPS}>
                      <MenuItem value="">Select</MenuItem>
                      {availableCountries.map((country) => (
                        <MenuItem key={country} value={country}>
                          {country}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  {/* Associate Name */}
                  <TableCell sx={{ width: 280, minWidth: 280, verticalAlign: "top" }}>
                    <TextField select size="small" fullWidth label="Associate Name" value={row.associateName} onChange={(e) => updateAssociateName(row.id, e.target.value)} disabled={isView || !row.country || associates.length === 1} sx={FIXED_SELECT_FIELD_SX}>
                      <MenuItem value="">Select</MenuItem>
                      {associates.map((assoc) => (
                        <MenuItem key={assoc.name} value={assoc.name}>
                          {assoc.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  {/* Emails (Read-only) */}
                  <TableCell sx={{ width: 320, minWidth: 320, verticalAlign: "top" }}>
                    <Box display="flex" flexDirection="column" gap={1}>
                      {row.associateEmails.map((email, i) => (
                        <TextField
                          key={i}
                          size="small"
                          fullWidth
                          value={email}
                          InputProps={{
                            readOnly: true,
                          }}
                          disabled
                        />
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* Add Row */}
      {!isView && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button variant="outlined" onClick={addRow}>
            Add More
          </Button>
        </Box>
      )}

      {/* Actions — disabled until the form has unsaved changes */}
      {!isView && (
        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={handleCancel} disabled={isSaving || !isDirty}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveData} disabled={isSaving || !isDirty}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ForeignAssociates;
